<template>
  <div class="pa-10">

    <!-- ── Back ── -->
    <div
      class="back-link mb-5"
      @click="$router.push(`/${basePath}/monitor-students`)"
    >
      <v-icon size="16" class="mr-1">mdi-arrow-left</v-icon>
      Back to Student List
    </div>

    <!-- ── Loading ── -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else-if="student">

      <!-- ══ Top row: Profile + Deployment ══ -->
      <v-row class="mb-5" align="stretch">

        <!-- Profile card -->
        <v-col cols="12" md="8">
          <div class="profile-card h-100">
            <div class="profile-body">

              <!-- Avatar + Name + Status -->
              <div class="profile-top-row">
                <div class="profile-avatar">{{ initials }}</div>
                <div class="profile-info">
                  <div class="d-flex justify-space-between align-start gap-2 mt-2">
                    <div class="profile-name">{{ student.name }}</div>
                    <span class="status-pill flex-shrink-0" :style="statusStyle(student.internship_status)">
                      {{ statusLabel(student.internship_status) }}
                    </span>
                  </div>
                  <div class="d-flex justify-space-between align-center mt-2
                  ">
                    <div class="profile-email">
                      <v-icon size="13" color="#64748b">mdi-email-outline</v-icon>
                      {{ student.email }}
                      <template v-if="student.program_abbr || student.program">
                        <span class="meta-dot">·</span>
                        {{ student.program_abbr || student.program }}
                      </template>
                    </div>
                    <span class="profile-school-id">{{ student.school_id }}</span>
                  </div>
                </div>
              </div>

              <!-- Stats strip -->
              <div class="stats-strip mt-4">
                <div class="stat-cell">
                  <div class="stat-icon-wrap stat-icon-wrap--slate">
                    <v-icon size="15" color="#475569">mdi-format-list-checks</v-icon>
                  </div>
                  <div class="stat-num">{{ getProgress.total }}</div>
                  <div class="stat-lbl">Total</div>
                </div>
                <div class="stat-divider"></div>
                <div class="stat-cell">
                  <div class="stat-icon-wrap stat-icon-wrap--green">
                    <v-icon size="15" color="#16a34a">mdi-check-circle-outline</v-icon>
                  </div>
                  <div class="stat-num stat-num--green">{{ getProgress.submitted }}</div>
                  <div class="stat-lbl">Completed</div>
                </div>
                <div class="stat-divider"></div>
                <div class="stat-cell">
                  <div class="stat-icon-wrap stat-icon-wrap--amber">
                    <v-icon size="15" color="#d97706">mdi-clock-outline</v-icon>
                  </div>
                  <div class="stat-num stat-num--amber">{{ getProgress.total - getProgress.submitted }}</div>
                  <div class="stat-lbl">Pending</div>
                </div>
              </div>

            </div>
          </div>
        </v-col>

        <!-- Deployment card -->
        <v-col cols="12" md="4">
          <div class="detail-card h-100 d-flex flex-column">
            <div class="card-heading mb-4">
              <div class="card-heading-icon card-heading-icon--blue">
                <v-icon size="15" color="#3b82f6">mdi-office-building-outline</v-icon>
              </div>
              Deployment Details
            </div>

            <template v-if="student.organization_name">
              <div class="deploy-field mb-3">
                <div class="deploy-field-row">
                  <v-icon size="14" color="#94a3b8">mdi-domain</v-icon>
                  <div class="deploy-label">Company</div>
                </div>
                <div class="deploy-value">{{ student.organization_name }}</div>
              </div>
              <div class="deploy-field">
                <div class="deploy-field-row">
                  <v-icon size="14" color="#94a3b8">mdi-calendar-start</v-icon>
                  <div class="deploy-label">Start Date</div>
                </div>
                <div class="deploy-value">{{ formatDate(student.organization_deployed_date) }}</div>
              </div>
            </template>

            <div v-else class="deploy-empty">
              <v-icon size="32" color="#cbd5e1">mdi-office-building-off-outline</v-icon>
              <span>Not yet deployed</span>
            </div>
          </div>
        </v-col>

      </v-row>

      <!-- ══ Main Two-Column ══ -->
      <v-row>

        <!-- LEFT: Requirements Checklist -->
        <v-col cols="12" md="8">
          <div class="detail-card">

            <!-- Checklist header -->
            <div class="d-flex justify-space-between align-center mb-4">
              <div class="card-heading">
                <div class="card-heading-icon card-heading-icon--green">
                  <v-icon size="15" color="#16a34a">mdi-file-document-check-outline</v-icon>
                </div>
                Requirements Checklist
              </div>
              <div class="d-flex align-center ga-2">
                <span class="pct-label">{{ progressPct }}%</span>
                <div class="mini-bar">
                  <div
                    class="mini-bar-fill"
                    :style="{ width: progressPct + '%', backgroundColor: progressBarColor }"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Requirement rows -->
            <div
              v-for="(req, idx) in student.requirements"
              :key="req.requirement_name"
            >
              <div class="req-row">
                <div class="req-icon-wrap" :class="reqIconClass(req.status)">
                  <v-icon size="17" :color="reqIconColor(req.status)">{{ reqIcon(req.status) }}</v-icon>
                </div>
                <div class="req-info flex-grow-1">
                  <div class="req-name">{{ req.requirement_name }}</div>
                  <div class="req-date">
                    {{ formatDate(req.submission?.createdAt || req.submission?.created_at) }}
                  </div>
                </div>
                <span class="req-pill" :style="reqStyle(req.status)">
                  {{ reqLabel(req.status) }}
                </span>
              </div>
              <v-divider v-if="idx < student.requirements.length - 1" />
            </div>

            <div v-if="!student.requirements || !student.requirements.length" class="req-empty">
              <v-icon size="32" color="#cbd5e1">mdi-file-document-off-outline</v-icon>
              <span>No requirements found.</span>
            </div>

          </div>
        </v-col>

        <!-- RIGHT: Actions + Timeline -->
        <v-col cols="12" md="4">

          <!-- Actions -->
          <div class="detail-card mb-4">
            <div class="card-heading mb-3">
              <div class="card-heading-icon card-heading-icon--slate">
                <v-icon size="15" color="#475569">mdi-lightning-bolt-outline</v-icon>
              </div>
              Quick Actions
            </div>
            <div class="action-item">
              <div class="action-icon action-icon--violet">
                <v-icon size="15" color="#7c3aed">mdi-clipboard-check-outline</v-icon>
              </div>
              <span class="action-label">View Skills Assessment Result</span>
              <v-icon size="14" color="#cbd5e1" class="ml-auto">mdi-chevron-right</v-icon>
            </div>
            <v-divider />
            <div class="action-item">
              <div class="action-icon action-icon--teal">
                <v-icon size="15" color="#0d9488">mdi-clock-outline</v-icon>
              </div>
              <span class="action-label">View Attendance Log</span>
              <v-icon size="14" color="#cbd5e1" class="ml-auto">mdi-chevron-right</v-icon>
            </div>
          </div>

          <!-- Timeline -->
          <div class="detail-card">
            <div class="card-heading mb-4">
              <div class="card-heading-icon card-heading-icon--slate">
                <v-icon size="15" color="#475569">mdi-timeline-outline</v-icon>
              </div>
              Activity Timeline
            </div>
            <div class="timeline">

              <div v-if="student.organization_deployed_date" class="tl-item">
                <div class="tl-track">
                  <div class="tl-dot tl-dot--green"></div>
                  <div class="tl-line"></div>
                </div>
                <div class="tl-content">
                  <div class="tl-title">Deployed to {{ student.organization_name || 'Company' }}</div>
                  <div class="tl-date">{{ formatDate(student.organization_deployed_date) }}</div>
                </div>
              </div>

              <div v-if="latestSubmission" class="tl-item">
                <div class="tl-track">
                  <div class="tl-dot tl-dot--blue"></div>
                  <div class="tl-line"></div>
                </div>
                <div class="tl-content">
                  <div class="tl-title">Requirements Updated</div>
                  <div class="tl-date">{{ timeAgo(latestSubmission) }}</div>
                </div>
              </div>

              <div class="tl-item">
                <div class="tl-track">
                  <div class="tl-dot tl-dot--gray"></div>
                </div>
                <div class="tl-content">
                  <div class="tl-title">Account Created</div>
                  <div class="tl-date">{{ timeAgo(student.createdAt || student.created_at) }}</div>
                </div>
              </div>

            </div>
          </div>

        </v-col>
      </v-row>

    </template>

    <!-- Not found -->
    <div v-else class="text-center text-grey py-12">
      Student data not found.
    </div>

  </div>
</template>

<script>
export default {
  name: "MonitorStudentDetail",

  data: () => ({
    student: null,
    loading: false,
  }),

  computed: {
    basePath() {
      const typePathMap = {
        3: "ojt-coordinator", 4: "instructor", 5: "dean",
        6: "vpaa", 10: "program-chair",
      };
      const userTypeId = this.$store.state.user?.user?.usertypeID;
      return typePathMap[userTypeId] || "program-chair";
    },

    initials() {
      if (!this.student?.name) return "?";
      return this.student.name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    },

    getProgress() {
      const reqs = this.student?.requirements || [];
      return {
        submitted: reqs.filter((r) => r.status == 2).length,
        total: reqs.length,
      };
    },

    progressPct() {
      const { submitted, total } = this.getProgress;
      if (!total) return 0;
      return Math.round((submitted / total) * 100);
    },

    progressBarColor() {
      const pct = this.progressPct;
      if (pct >= 75) return "#22c55e";
      if (pct >= 50) return "#f59e0b";
      return "#ef4444";
    },

    latestSubmission() {
      const reqs = this.student?.requirements || [];
      const dates = reqs
        .map((r) => r.submission?.createdAt || r.submission?.created_at)
        .filter(Boolean)
        .map((d) => new Date(d).getTime());
      if (!dates.length) return null;
      return new Date(Math.max(...dates));
    },
  },

  created() {
    const stored = sessionStorage.getItem("monitorStudent");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (String(parsed.id) === String(this.$route.params.id)) {
          this.student = parsed;
          return;
        }
      } catch (e) {
        console.warn("Failed to parse stored student", e);
      }
    }
    this.loadFromApi();
  },

  methods: {
    loadFromApi() {
      this.loading = true;
      this.axiosCall("/program/getProgramsBatchByInstitute", "GET")
        .then((res) => {
          if (res?.data?.length) {
            return this.axiosCall("/student/getDeanView/" + res.data[0].id, "GET");
          }
        })
        .then((res) => {
          if (res?.data) {
            const found = res.data.find(
              (s) => String(s.id) === String(this.$route.params.id)
            );
            if (found) this.student = { ...found, name: this.toTitleCase(found.name) };
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    statusLabel(status) {
      const map = { 1: "Searching", 2: "Searching", 3: "Deployed", 4: "Completed", 5: "Pulled Out", 6: "Terminated" };
      return map[status] || "For Matching";
    },

    statusStyle(status) {
      const map = {
        1: { backgroundColor: "#fef9c3", color: "#b45309" },
        2: { backgroundColor: "#fef9c3", color: "#b45309" },
        3: { backgroundColor: "#dcfce7", color: "#16a34a" },
        4: { backgroundColor: "#d1fae5", color: "#059669" },
        5: { backgroundColor: "#ffedd5", color: "#c2410c" },
        6: { backgroundColor: "#fee2e2", color: "#dc2626" },
      };
      return map[status] || { backgroundColor: "#f1f5f9", color: "#374151" };
    },

    reqLabel(status) {
      if (status == 2) return "Approved";
      if (status == 3) return "Rejected";
      if (status == 1) return "Pending";
      return "Not Submitted";
    },

    reqStyle(status) {
      if (status == 2) return { backgroundColor: "#dcfce7", color: "#16a34a" };
      if (status == 3) return { backgroundColor: "#fee2e2", color: "#dc2626" };
      if (status == 1) return { backgroundColor: "#fef9c3", color: "#b45309" };
      return { backgroundColor: "#f1f5f9", color: "#94a3b8" };
    },

    reqIcon(status) {
      if (status == 2) return "mdi-check-circle-outline";
      if (status == 3) return "mdi-close-circle-outline";
      if (status == 1) return "mdi-clock-outline";
      return "mdi-file-outline";
    },

    reqIconColor(status) {
      if (status == 2) return "#16a34a";
      if (status == 3) return "#dc2626";
      if (status == 1) return "#f59e0b";
      return "#94a3b8";
    },

    reqIconClass(status) {
      if (status == 2) return "icon-wrap--green";
      if (status == 3) return "icon-wrap--red";
      if (status == 1) return "icon-wrap--amber";
      return "icon-wrap--gray";
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (isNaN(d)) return "—";
      return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    },

    timeAgo(dateStr) {
      if (!dateStr) return "—";
      const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
      const months = Math.floor(days / 30);
      return `${months} month${months > 1 ? "s" : ""} ago`;
    },

    sendEmail() {
      if (this.student?.email) {
        window.location.href = `mailto:${this.student.email}`;
      }
    },
  },
};
</script>

<style scoped>
/* ── Back link ── */
.back-link {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: color 0.15s;
}
.back-link:hover { color: #111827; }

/* ── Shared card base ── */
.profile-card,
.detail-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}
.detail-card { padding: 24px; overflow: visible; }

/* ── Card headings ── */
.card-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}
.card-heading-icon {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.card-heading-icon--blue   { background: #eff6ff; }
.card-heading-icon--green  { background: #f0fdf4; }
.card-heading-icon--slate  { background: #f1f5f9; }

/* ══ Profile Card ══ */
.profile-body { padding: 24px; }

.profile-top-row {
  display: flex;
  align-items: flex-start;
  gap: 18px;
}

.profile-avatar {
  width: 68px;
  height: 68px;
  border-radius: 14px;
  background: #eff6ff;
  border: 1.5px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 22px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: -0.5px;
}

.profile-info { flex: 1; min-width: 0; }

.profile-name {
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.meta-dot { margin: 0 6px; color: #d1d5db; }

.profile-school-id {
  font-size: 12.5px;
  color: #94a3b8;
  font-weight: 500;
  flex-shrink: 0;
}

.profile-email {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

/* ── Stats strip ── */
.stats-strip {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}
.stat-cell {
  flex: 1;
  text-align: center;
  padding: 14px 12px;
}
.stat-divider {
  width: 1px;
  height: 40px;
  background: #e5e7eb;
  flex-shrink: 0;
}
.stat-icon-wrap {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 6px;
}
.stat-icon-wrap--slate { background: #f1f5f9; }
.stat-icon-wrap--green { background: #f0fdf4; }
.stat-icon-wrap--amber { background: #fffbeb; }
.stat-num {
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  line-height: 1;
}
.stat-num--green { color: #16a34a; }
.stat-num--amber { color: #d97706; }
.stat-lbl {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 4px;
}

/* ── Status pill ── */
.status-pill {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 99px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}

/* ── Deployment fields ── */
.deploy-field {
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.deploy-field-row {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
}
.deploy-label {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.deploy-value {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.deploy-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 0;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 500;
  flex: 1;
  justify-content: center;
}

/* ── Progress bar ── */
.pct-label {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  white-space: nowrap;
}
.mini-bar {
  width: 90px;
  height: 7px;
  background: #e5e7eb;
  border-radius: 99px;
  overflow: hidden;
  flex-shrink: 0;
}
.mini-bar-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.4s ease;
}

/* ── Requirement rows ── */
.req-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 0;
}
.req-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.icon-wrap--green { background: #f0fdf4; }
.icon-wrap--red   { background: #fff1f2; }
.icon-wrap--amber { background: #fffbeb; }
.icon-wrap--gray  { background: #f8fafc; }

.req-info { min-width: 0; }
.req-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
.req-date {
  font-size: 11.5px;
  color: #94a3b8;
  margin-top: 2px;
}
.req-pill {
  display: inline-block;
  padding: 3px 11px;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}
.req-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;
  color: #94a3b8;
  font-size: 13px;
}

/* ── Action items ── */
.action-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 0;
  cursor: pointer;
  transition: background 0.12s;
  border-radius: 8px;
}
.action-item:hover { background: #f8fafc; margin: 0 -8px; padding: 13px 8px; }
.action-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.action-icon--blue   { background: #eff6ff; }
.action-icon--violet { background: #f5f3ff; }
.action-icon--teal   { background: #f0fdfa; }
.action-label {
  flex: 1;
  font-size: 13.5px;
  font-weight: 600;
  color: #374151;
}

/* ── Timeline ── */
.timeline { display: flex; flex-direction: column; }
.tl-item {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}
.tl-track {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 10px;
  margin-top: 4px;
}
.tl-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.tl-dot--green { background: #22c55e; box-shadow: 0 0 0 3px #dcfce7; }
.tl-dot--blue  { background: #3b82f6; box-shadow: 0 0 0 3px #dbeafe; }
.tl-dot--gray  { background: #cbd5e1; box-shadow: 0 0 0 3px #f1f5f9; }
.tl-line {
  width: 2px;
  flex: 1;
  min-height: 24px;
  background: #e5e7eb;
  margin: 5px 0;
}
.tl-content { padding-bottom: 20px; }
.tl-title { font-size: 13.5px; font-weight: 600; color: #111827; line-height: 1.35; }
.tl-date  { font-size: 12px; color: #94a3b8; margin-top: 2px; }
</style>
